import React from 'react';
import { Link } from 'react-router-dom';
import { Cog, TrendingUp, Eye, Bot, Database, Shield, ArrowRight, CheckCircle } from 'lucide-react';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Schema } from '../../components/Schema';
import { useBanner } from '../../contexts/BannerContext';

export const ManufacturingAIPage: React.FC = () => {
  const { isBannerVisible, bannerHeight } = useBanner();

  const manufacturingServices = [
    {
      icon: <TrendingUp className="w-6 h-6" />,
      title: "Predictive Maintenance",
      description: "AI-powered equipment monitoring and predictive maintenance designed to help reduce downtime and extend equipment lifespan. Results may vary based on equipment conditions and implementation.",
      features: ["Equipment Monitoring", "Failure Prediction", "Maintenance Scheduling", "Cost Optimization"]
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Quality Control Automation",
      description: "Computer vision systems for automated quality inspection, defect detection, and product verification.",
      features: ["Visual Inspection", "Defect Detection", "Compliance Monitoring", "Real-time Alerts"]
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Supply Chain Optimization",
      description: "AI-driven supply chain management, demand forecasting, and inventory optimization solutions.",
      features: ["Demand Forecasting", "Inventory Management", "Supplier Analytics", "Risk Assessment"]
    },
    {
      icon: <Bot className="w-6 h-6" />,
      title: "Production Automation",
      description: "Intelligent automation systems for manufacturing processes, robotics integration, and workflow optimization.",
      features: ["Process Automation", "Robotics Integration", "Workflow Optimization", "Production Planning"]
    },
    {
      icon: <Cog className="w-6 h-6" />,
      title: "Energy Efficiency",
      description: "AI-powered energy management systems to optimize consumption and reduce operational costs.",
      features: ["Energy Monitoring", "Usage Optimization", "Cost Reduction", "Sustainability Metrics"]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Industrial IoT Security",
      description: "Comprehensive cybersecurity solutions for connected manufacturing environments and industrial systems.",
      features: ["Network Security", "Device Management", "Threat Detection", "Compliance Monitoring"]
    }
  ];

  const benefits = [
    "Potential to reduce equipment downtime*",
    "Work toward improving product quality and consistency",
    "Optimize energy consumption and operational efficiency*",
    "Streamline supply chain operations",
    "Enhance worker safety and compliance monitoring",
    "Support increased overall equipment effectiveness (OEE)*"
  ];

  const industries = [
    {
      title: "Automotive Manufacturing",
      description: "AI solutions for automotive assembly lines, quality control, and supply chain management.",
      applications: ["Assembly Line Optimization", "Parts Quality Inspection", "Predictive Maintenance", "Supply Chain Analytics"]
    },
    {
      title: "Food & Beverage",
      description: "Specialized AI for food safety, quality control, and production optimization in food manufacturing.",
      applications: ["Food Safety Monitoring", "Quality Assurance", "Batch Optimization", "Compliance Reporting"]
    },
    {
      title: "Electronics Manufacturing",
      description: "Precision AI solutions for electronics assembly, testing, and component verification.",
      applications: ["PCB Inspection", "Component Testing", "Assembly Verification", "Defect Analysis"]
    },
    {
      title: "Chemical & Pharmaceutical",
      description: "AI-driven process optimization, safety monitoring, and regulatory compliance solutions.",
      applications: ["Process Control", "Safety Monitoring", "Batch Analysis", "Regulatory Compliance"]
    }
  ];

  return (
    <>
      <Schema 
        type="service" 
        data={{
          name: "Manufacturing AI Solutions",
          description: "AI-powered manufacturing solutions including predictive maintenance, quality control, supply chain optimization, and industrial automation",
          serviceType: "Manufacturing Technology",
          offerDescription: "Comprehensive AI solutions for modern manufacturing operations"
        }} 
      />
      <Breadcrumb />
      <main className="pb-20">
        {/* Hero Section */}
        <section 
          className="px-4 sm:px-6 lg:px-8 mb-20"
          style={{ 
            paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px'
          }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
                <Cog className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent tracking-tight">
                Smart Manufacturing with AI
              </h1>
              <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
                Transform your manufacturing operations with AI-powered predictive maintenance, quality control, and production optimization solutions.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Get Manufacturing AI Consultation
                  <ArrowRight className="w-5 h-5 ml-2 inline" />
                </Link>
                <Link
                  to="/case-studies"
                  className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
                >
                  View Success Stories
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Proven Manufacturing Benefits
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3 p-6 bg-white/80 dark:bg-white/5 rounded-xl border border-gray-200 dark:border-white/10">
                  <CheckCircle className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                  <p className="text-gray-700 dark:text-gray-300">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Comprehensive Manufacturing AI Services
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                From predictive maintenance to quality control, we provide end-to-end AI solutions for modern manufacturing.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {manufacturingServices.map((service, index) => (
                <div key={index} className="group p-8 bg-white/80 dark:bg-white/5 rounded-3xl border border-gray-200 dark:border-white/10 hover:shadow-xl hover:scale-105 transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mb-6 text-white group-hover:scale-110 transition-transform duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                        <CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industries Section */}
        <section className="px-4 sm:px-6 lg:px-8 mb-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Industry-Specific Solutions
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
                Tailored AI solutions for different manufacturing sectors and their unique requirements.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <div key={index} className="p-8 bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/10 dark:to-transparent rounded-3xl border border-blue-200 dark:border-blue-500/20">
                  <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
                    {industry.title}
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-6">
                    {industry.description}
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {industry.applications.map((app, aIndex) => (
                      <div key={aIndex} className="flex items-center text-sm text-blue-700 dark:text-blue-300">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        {app}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Ready to Revolutionize Your Manufacturing?
              </h2>
              <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
                Join manufacturers across Michigan, Ohio, and the Midwest who are already using AI to reduce costs, improve quality, and increase efficiency.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
                >
                  Schedule Free Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <button
                  onClick={() => window.open("https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled", "_blank")}
                  className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
                >
                  Book Demo Call
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Disclaimer */}
        <section className="px-4 sm:px-6 lg:px-8 pb-12 pt-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6 text-sm text-gray-600 dark:text-gray-400">
              <p className="mb-2">
                <strong>*Disclaimer:</strong> AI implementation success depends on various factors including current systems, implementation quality, user adoption, and business environment. Results may vary based on individual circumstances and specific manufacturing environments. All projections and estimates are subject to change. Consult with our team for assessments specific to your situation.
              </p>
              <p>
                This content is for informational purposes only and does not constitute professional advice. Individual results may vary based on numerous factors specific to each business and implementation environment.
              </p>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};